import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { sheetViewAPI } from '../api/client';

const SIZES = ['36', '37', '38', '39', '40', '41', '42', '43', '44', '45', '46'];
const PAGE = 30;
const colLetter = (i) => String.fromCharCode(65 + i);

// Плоский список рядів з нумерацією, як у Google-таблиці: блок = 17 рядів
function buildRows(blocks, ncols) {
  const rows = [];
  const push = (r) => rows.push({ n: rows.length + 1, ...r });
  blocks.forEach(b => {
    const inStock = new Set();
    b.suppliers.forEach(s => Object.entries(s.sizes).forEach(([sz, q]) => { if (q > 0) inStock.add(sz); }));
    const sup = (i) => b.suppliers[i - 2];
    push({ cells: [] });
    push({ cells: [null, { text: b.sku, cls: 'sv-art' }] });
    push({ cells: [] });
    push({ cells: [null, null, ...Array.from({ length: ncols - 2 }, (_, k) => sup(k + 2) ? { text: 'ФОТО', link: sup(k + 2).link, cls: 'sv-lnk' } : null)] });
    push({ cells: [null, null, ...Array.from({ length: ncols - 2 }, (_, k) => sup(k + 2) ? { text: sup(k + 2).name, cls: 'sv-lnk' } : null)] });
    push({ cells: [{ text: b.name, cls: 'sv-name' }, { text: 'Размеры', cls: 'sv-head' },
      ...Array.from({ length: ncols - 2 }, (_, k) => sup(k + 2) ? { text: sup(k + 2).price != null ? Math.round(sup(k + 2).price) : '—', cls: 'sv-price' } : null)] });
    SIZES.forEach((sz, ri) => {
      push({
        photo: ri === 0 ? b.photo || true : undefined,
        cells: [undefined, { text: sz, cls: inStock.has(sz) ? 'sv-size sv-size--on' : 'sv-size' },
          ...Array.from({ length: ncols - 2 }, (_, k) => { const s = sup(k + 2); return s && s.sizes[sz] > 0 ? { text: s.sizes[sz] } : null; })],
      });
    });
    push({ cells: [] });
  });
  return rows;
}

export default function SheetViewTab({ standalone = false }) {
  const [tabs, setTabs] = useState([]);
  const [tab, setTab] = useState(() => { try { return localStorage.getItem('sv_tab') || ''; } catch { return ''; } });
  const [blocks, setBlocks] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState(null);       // {row, col}: null = не задано
  const [full, setFull] = useState(false);
  const [menu, setMenu] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    sheetViewAPI.tabs().then(r => {
      setTabs(r.data);
      if (!r.data.find(t => t.key === tab) && r.data[0]) setTab(r.data[0].key);
    });
  }, []);

  const load = useCallback((key, offset) => {
    setLoading(true);
    sheetViewAPI.blocks(key, { offset, limit: PAGE }).then(r => {
      setTotal(r.data.total);
      setBlocks(prev => offset === 0 ? r.data.blocks : [...prev, ...r.data.blocks]);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!tab) return;
    try { localStorage.setItem('sv_tab', tab); } catch {}
    setBlocks([]); setSel(null);
    load(tab, 0);
  }, [tab, load]);

  useEffect(() => {
    // на iOS Fullscreen API немає — там працює лише CSS-режим (full керується кнопкою)
    const h = () => { if (!document.fullscreenElement) setFull(false); };
    document.addEventListener('fullscreenchange', h);
    return () => document.removeEventListener('fullscreenchange', h);
  }, []);

  const toggleFull = () => {
    if (full) {
      setFull(false);
      if (document.fullscreenElement) document.exitFullscreen?.();
    } else {
      setFull(true);
      try { wrapRef.current?.requestFullscreen?.()?.catch?.(() => {}); } catch {}
    }
  };
  const isFull = full || standalone;

  const ncols = useMemo(() => Math.max(6, 2 + Math.max(0, ...blocks.map(b => b.suppliers.length))), [blocks]);
  const rows = useMemo(() => buildRows(blocks, ncols), [blocks, ncols]);
  const onBody = (e) => {
    const t = e.target.closest('[data-r]');
    if (t) setSel({ row: +t.dataset.r, col: +t.dataset.c });
  };

  const rowOn = (n) => sel && sel.row === n && sel.col === -1;
  const colOn = (c) => sel && sel.col === c && sel.row === -1;

  return (
    <div className={`sv-wrap${isFull ? ' sv-wrap--full' : ''}`} ref={wrapRef}>
      <div className="sv-bar">
        <span className="sv-bar-info">{tab} · {blocks.length} з {total}</span>
        {!standalone && <button className="sv-fs" onClick={toggleFull}>{full ? '✕ Вийти з повного екрана' : '⛶ На весь екран'}</button>}
      </div>
      <div className="sv-body" onClick={onBody}>
        <table className="sv-grid">
          <colgroup>
            <col style={{ width: 46 }} />
            {Array.from({ length: ncols }, (_, i) => <col key={i} style={{ width: i === 0 ? 270 : i === 1 ? 84 : 92 }} />)}
          </colgroup>
          <thead>
            <tr>
              <th className="sv-corner" onClick={() => setSel(null)} />
              {Array.from({ length: ncols }, (_, c) => (
                <th key={c} className={`sv-colh${colOn(c) || (sel && sel.col === c && sel.row > 0) ? ' sv-colh--on' : ''}`}
                    onClick={(e) => { e.stopPropagation(); setSel({ row: -1, col: c }); }}>{colLetter(c)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.n} className={rowOn(r.n) ? 'sv-row--sel' : ''}>
                <th className={`sv-rowh${sel && sel.row === r.n ? ' sv-rowh--on' : ''}`}
                    onClick={(e) => { e.stopPropagation(); setSel({ row: r.n, col: -1 }); }}>{r.n}</th>
                {r.cells.map((cell, c) => {
                  if (cell === undefined && c === 0) {
                    if (r.photo === undefined) return null;
                    return (
                      <td key={c} rowSpan={SIZES.length} data-r={r.n} data-c={c}
                          className={`sv-c sv-photo${colOn(c) ? ' sv-c--col' : ''}`}>
                        {typeof r.photo === 'string' ? <img src={r.photo} alt="" loading="lazy" /> : null}
                      </td>
                    );
                  }
                  const on = sel && sel.row === r.n && sel.col === c;
                  const cls = ['sv-c', cell?.cls || '', colOn(c) ? 'sv-c--col' : '', on ? 'sv-c--cell' : ''].join(' ');
                  return (
                    <td key={c} data-r={r.n} data-c={c} className={cls}>
                      {cell?.link ? <a href={cell.link} target="_blank" rel="noreferrer">{cell.text}</a> : cell?.text}
                    </td>
                  );
                })}
                {Array.from({ length: Math.max(0, ncols - r.cells.length) }, (_, k) => {
                  const c = r.cells.length + k;
                  return <td key={`e${c}`} data-r={r.n} data-c={c} className={`sv-c${colOn(c) ? ' sv-c--col' : ''}`} />;
                })}
              </tr>
            ))}
          </tbody>
        </table>
        {loading && <div className="sv-note">Завантаження…</div>}
        {!loading && blocks.length < total && (
          <button className="rt-btn-primary sv-more" onClick={() => load(tab, blocks.length)}>
            Показати ще ({blocks.length} з {total})
          </button>
        )}
      </div>
      <div className="sv-tabs">
        <button className="sv-tab sv-tab--menu" title="Усі листи" onClick={() => setMenu(m => !m)}>☰</button>
        {menu && (
          <div className="sv-menu">
            {tabs.map(t => (
              <div key={t.key} className={`sv-menu-item${t.key === tab ? ' sv-menu-item--on' : ''}`}
                   onClick={() => { setTab(t.key); setMenu(false); }}>
                {t.label} <span className="sv-count">{t.count}</span>
              </div>
            ))}
          </div>
        )}
        <div className="sv-tabs-scroll">
          {tabs.map(t => (
            <button key={t.key} ref={t.key === tab ? (el) => el?.scrollIntoView({ block: 'nearest', inline: 'center' }) : null}
                    className={`sv-tab${t.key === tab ? ' sv-tab--active' : ''}`} onClick={() => setTab(t.key)}>
              {t.label} <span className="sv-count">{t.count}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
